import { css } from '@emotion/react';
import { Box, Text } from '@inflearn/ds-react-next';
import { useTranslation } from 'next-i18next';

import { SectionHead } from './SectionHead';
import { LANDING_COLOR, LANDING_FONT, LANDING_MOBILE_MQ, LANDING_SPACE } from './constants';

/** 기업명 대신 이니셜만 노출한다 — 타일 색은 각 사 대표색 */
const COMPANY_TILES = [
  { key: 'n', initial: 'N', bg: '#03C75A', color: '#fff' },
  { key: 'k', initial: 'K', bg: '#FEE500', color: '#191919' },
  { key: 'l', initial: 'L', bg: '#06C755', color: '#fff' },
  { key: 'inflearn', initial: '', bg: '#00C471', color: '#fff' },
  { key: 'c', initial: 'C', bg: '#E52528', color: '#fff' },
  { key: 'b', initial: 'B', bg: '#2AC1BC', color: '#fff' },
  { key: 'd', initial: 'D', bg: '#FF6F0F', color: '#fff' },
  { key: 't', initial: 'T', bg: '#0064FF', color: '#fff' },
] as const;

const POINTS = [
  {
    key: 'pattern',
    leadKey: 'ai_interviews.landing.past.pattern.lead',
    titleKey: 'ai_interviews.landing.past.pattern.title',
  },
  {
    key: 'criteria',
    leadKey: 'ai_interviews.landing.past.criteria.lead',
    titleKey: 'ai_interviews.landing.past.criteria.title',
  },
] as const;

const BRANCH_COLOR = '#C4CAD0';
const BRANCH_HEIGHT = 56;

const InflearnSymbol = () => (
  <svg viewBox="0 12.6 20.4 20.6" width={24} height={24} aria-hidden>
    <path
      fill="#fff"
      d="M2.2526 22.9606C2.2526 18.5668 5.81978 14.9921 10.2044 14.9921C14.589 14.9921 18.1562 18.5668 18.1562 22.9606V29.7649L11.0462 22.5183L12.6228 17.9893C12.7734 17.5566 12.5197 17.1038 12.0309 16.933C11.5668 16.7707 11.0553 16.9803 10.9121 17.3914L9.63002 21.0747L7.3566 18.7578C7.02024 18.4207 6.45089 18.3947 6.08855 18.7578C5.75193 19.0948 5.72621 19.6654 6.08855 20.0287L11.5342 25.5788H7.6153C7.12361 25.5788 6.70959 25.9939 6.70959 26.4866C6.70959 27.0054 7.12361 27.3945 7.6153 27.3945H13.2576C13.2759 27.3945 13.2926 27.3903 13.3104 27.3895L16.7835 30.9291H10.2044C5.81978 30.9291 2.2526 27.3544 2.2526 22.9606ZM10.2044 13C4.71474 13 0.264648 17.4594 0.264648 22.9606C0.264648 28.4618 4.71474 32.9212 10.2044 32.9212H20.1442V22.9606C20.1442 17.4594 15.6941 13 10.2044 13Z"
    />
  </svg>
);

/** STEP 1 — 실무 면접 기출 */
export const PastQuestions = () => {
  const { t } = useTranslation();

  return (
    <Box component="section" css={styles.section} data-reveal aria-labelledby="ai-past-title">
      <Box css={styles.inner}>
        <SectionHead id="ai-past-title" step={1} titleKey="ai_interviews.landing.past.title" />

        <Box css={styles.tree}>
          <Box css={styles.bubble}>
            <Box
              component="ul"
              css={styles.tiles}
              aria-label={t('ai_interviews.landing.past.companies_aria')}
            >
              {COMPANY_TILES.map((tile) => (
                <Box
                  key={tile.key}
                  component="li"
                  css={styles.tile}
                  style={{ background: tile.bg, color: tile.color }}
                  aria-label={
                    tile.key === 'inflearn' ? t('ai_interviews.landing.past.inflearn') : undefined
                  }
                >
                  {tile.key === 'inflearn' ? (
                    <InflearnSymbol />
                  ) : (
                    t('ai_interviews.landing.past.company', { initial: tile.initial })
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          <Box css={styles.branch} aria-hidden>
            <Box component="i" css={[styles.line, styles.stem]} />
            <Box component="i" css={[styles.line, styles.bar]} />
            <Box component="i" css={[styles.line, styles.leg, styles.legLeft]} />
            <Box component="i" css={[styles.line, styles.leg, styles.legRight]} />
          </Box>

          <Box css={styles.points}>
            {POINTS.map((point) => (
              <Box key={point.key} css={styles.point}>
                <Text css={styles.pointLead}>{t(point.leadKey)}</Text>
                <Text css={styles.pointTitle}>{t(point.titleKey)}</Text>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const styles = {
  section: css({
    background: '#fff',
    padding: `${LANDING_SPACE.sectionY} ${LANDING_SPACE.pageX}`,
  }),
  inner: css({
    maxWidth: 1200,
    margin: '0 auto',
  }),
  tree: css({
    maxWidth: 1000,
    margin: `${LANDING_SPACE.blockTop} auto 0`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  }),
  bubble: css({
    position: 'relative',
    padding: 12,
    [LANDING_MOBILE_MQ]: {
      padding: 8,
    },
    borderRadius: 24,
    border: '1px solid #E6E9EC',
    background: '#fff',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '50%',
      bottom: -13,
      width: 0,
      height: 0,
      transform: 'translateX(-50%)',
      borderLeft: '12px solid transparent',
      borderRight: '12px solid transparent',
      borderTop: '14px solid #fff',
      filter: 'drop-shadow(0 1px 0 #E6E9EC)',
    },
  }),
  tiles: css({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    margin: 0,
    padding: 0,
    listStyle: 'none',
    [LANDING_MOBILE_MQ]: {
      flexWrap: 'nowrap',
      gap: 4,
    },
  }),
  tile: css({
    display: 'grid',
    placeItems: 'center',
    width: 52,
    height: 52,
    borderRadius: 14,
    fontSize: LANDING_FONT.label,
    fontWeight: 800,
    [LANDING_MOBILE_MQ]: {
      width: 36,
      height: 36,
      borderRadius: 10,
      fontSize: 12,
      letterSpacing: '-0.04em',
      '& svg': {
        width: 18,
        height: 18,
      },
    },
  }),
  branch: css({
    position: 'relative',
    width: '100%',
    height: BRANCH_HEIGHT,
    [LANDING_MOBILE_MQ]: {
      height: 32,
    },
  }),
  line: css({
    position: 'absolute',
    display: 'block',
    background: BRANCH_COLOR,
  }),
  stem: css({
    left: '50%',
    top: 14,
    width: 2,
    height: (BRANCH_HEIGHT - 14) / 2,
    transform: 'translateX(-1px)',
    [LANDING_MOBILE_MQ]: {
      top: 14,
      height: 18,
    },
  }),
  /** 두 카드 중심(각 25%·75%)을 잇는 가로선 */
  bar: css({
    top: 14 + (BRANCH_HEIGHT - 14) / 2,
    left: 'calc(25% - 5px)',
    right: 'calc(25% - 5px)',
    height: 2,
    [LANDING_MOBILE_MQ]: {
      display: 'none',
    },
  }),
  leg: css({
    top: 14 + (BRANCH_HEIGHT - 14) / 2,
    bottom: 0,
    width: 2,
    [LANDING_MOBILE_MQ]: {
      display: 'none',
    },
  }),
  legLeft: css({
    left: 'calc(25% - 5px)',
  }),
  legRight: css({
    right: 'calc(25% - 5px)',
  }),
  points: css({
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 20,
    [LANDING_MOBILE_MQ]: {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  }),
  point: css({
    padding: 'clamp(24px, 1.5vw + 16px, 36px) 28px',
    borderRadius: 24,
    background: LANDING_COLOR.surface,
    textAlign: 'center',
  }),
  pointLead: css({
    color: LANDING_COLOR.body,
    fontSize: LANDING_FONT.introBody,
    lineHeight: 1.5,
    wordBreak: 'keep-all',
  }),
  pointTitle: css({
    marginTop: 6,
    color: LANDING_COLOR.ink,
    fontSize: LANDING_FONT.sectionTitleSm,
    fontWeight: 800,
    lineHeight: 1.35,
    letterSpacing: '-0.03em',
    wordBreak: 'keep-all',
  }),
};
